<template>
  <div class="container-payment flex-full">
    <div class="container-payment-inner">

      <!-- Details -->
      <article class="container-payment-details">

        <!-- Details title -->
        <div class="container-payment-details-inner">
          <img v-if="!pending" src="../assets/payment/error.svg" class="container-payment-details-icon" alt="Error icon" width="33" height="30" loading="eager" fetchpriority="high">
          <h1 class="container-payment-details-title"> {{ pending ? 'Платіж перевіряється' : 'Платіж відхилено' }} </h1>
        </div>
        <!---->

        <!-- Details text -->
        <!-- K2-3073: the bank may have taken the payment - no retry, so it is not paid twice. -->
        <p v-if="pending" class="container-payment-details-text"> {{ $t(beingCheckedMessage) }} </p>
        <template v-else>
          <p class="container-payment-details-text"> {{ failedMessage }} </p>
          <p class="container-payment-details-text"> Будь ласка, <a href="/contacts" class="link no-underline"> зателефонуйте </a> нам для узгодження подальших дій або спробуйте оплатити ще раз в <a href="/my-account" class="link no-underline"> особистому кабінеті </a> згідно інструкції. </p>
        </template>
        <!---->

      </article>
      <!---->

      <!-- Disclaimer -->
      <div v-if="!pending" class="container-payment-disclaimer">
        <div class="container-payment-disclaimer-block">
          <img
            src="../assets/payment/photo-1/image.png"
            srcset="../assets/payment/photo-1/image@2x.png 2x,
                    ../assets/payment/photo-1/image@3x.png 3x"
            class="container-payment-disclaimer-block-photo"
            alt="First step"
            width="1187"
            height="548"
            loading="eager"
            fetchpriority="high"
          >
          <span class="container-payment-disclaimer-block-circle"> 1 </span>
        </div>
        <div class="container-payment-disclaimer-block">
          <img
            src="../assets/payment/photo-2/bitmap.png"
            srcset="../assets/payment/photo-2/bitmap@2x.png 2x,
                    ../assets/payment/photo-2/bitmap@3x.png 3x"
            class="container-payment-disclaimer-block-photo"
            alt="Second step"
            width="1226"
            height="671"
            loading="eager"
            fetchpriority="high"
          >
          <span class="container-payment-disclaimer-block-circle"> 2 </span>
        </div>
      </div>
      <!---->

      <!-- Actions -->
      <div v-if="!pending && !!orders.length" class="container-payment-actions__root">
        <div v-for="(order) in orders" :key="order.id">
          <button-full
            class="pay-button"
            v-if="checkOnlinePayment(order.payment_method_code) && !order.is_paid"
            @click.native="pay(order.id)"
          >
            <span class="material-icons">credit_card</span>
            <span> {{ $t('Pay') }} #{{ order.connection_order_id }} </span>
          </button-full>
          <pay-order-modal v-if="!order.is_paid" :order="order" @calculateFee="() => {}" />
        </div>
      </div>
      <!---->
    </div>
  </div>
</template>

<script async>
import { mapGetters } from 'vuex';
import { isServer } from '@vue-storefront/core/helpers';
import { PAYMENT_BEING_CHECKED, translatePaymentMessage } from 'src/modules/ipay-payment/helpers/paymentMessages';
import { isPaymentPendingRoute } from 'src/modules/ipay-payment/helpers/paymentRecording';
const ButtonFull = () => import(/* webpackChunkName: "vsf-component-button-full" */ 'theme/components/theme/ButtonFull.vue');
const PayOrderModal = () => import(/* webpackChunkName: "vsf-component-pay-order-modal" */ 'theme/components/core/blocks/MyAccount/Order/PayOrderModal.vue');

export default {
  components: { PayOrderModal, ButtonFull },
  data () {
    return {
      image: '../assets/bg.png',
      orders: [],
      beingCheckedMessage: PAYMENT_BEING_CHECKED
    }
  },
  computed: {
    // vuestorefront-api's 3DS callback when the payment may have gone through.
    pending () {
      return isPaymentPendingRoute(this.$route)
    },
    // K2-3084: vuestorefront-api's English refusals (e.g. the 503) translated.
    failedMessage () {
      return translatePaymentMessage(this.getMessage, key => this.$t(key));
    },
    ...mapGetters({
      getMessage: 'checkout/getPaymentFailedMessage',
      paymentMethods: 'payment/allPaymentMethods'
    })
  },
  methods: {
    pay (orderId) {
      this.$bus.$emit('modal-show', `pay-order-${orderId}`)
    },
    checkOnlinePayment (payment) {
      let method = this.paymentMethods.find(m => m.code === payment)
      return method && !method.offline
    }
  },
  mounted () {
    if (!isServer) {
      const orders = JSON.parse(localStorage.getItem('shop/order/last-placing-orders-data'));
      if (Array.isArray(orders)) {
        this.orders = orders;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.container-payment {
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &-inner {
    width: 1152px;
    margin: 80px 0 40px 0;
  }

  &-details {
    width: 460px;
    margin-left: 100px;

    &-inner {
      margin-bottom: 20px;

      display: flex;
      align-items: center;
    }

    &-icon {
      width: 33px;
      height: 30px;
      margin-right: 10px;
    }

    &-title {
      margin: 0;

      color: #111;
      font-size: 34px;
      font-weight: 400;
      line-height: 0.9;
    }

    &-text {
      margin: 0;

      color: #111;
      font-size: 16px;
      font-weight: 400;

      & + & {
        margin-top: 10px;
      }

      & .link {
        color: #11bb2e;

        &:hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }
  }

  &-disclaimer {
    width: 750px;
    height: 440px;
    margin: 40px auto;
    position: relative;

    &-block {
      position: absolute;

      &:nth-child(1) {
        width: 620px;
        height: 280px;
        top: 0;
        right: 0;
      }

      &:nth-child(2) {
        width: 620px;
        height: 320px;
        left: 0;
        bottom: 0;
      }

      &-photo {
        width: 100%;
        height: 100%;
      }

      &-circle {
        width: 34px;
        height: 34px;
        position: absolute;
        top: -17px;
        left: 80px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        font-size: 16px;
        font-weight: 700;
        background: #11bb2e;
        border-radius: 50%;
      }
    }
  }

  &-actions {
    &__root {
      margin: 0 100px;
      display: flex;
      flex-direction: column;
      row-gap: 16px;
    }
  }

  @media only screen and (max-width: 1200px) {
    &-inner {
      width: 962px;
    }
  }

  @media only screen and (max-width: 991px) {
    &-disclaimer {
      width: 655px;
      height: 374px;

      &-block {
        &:nth-child(1) {
          width: calc(620px - 10%);
          height: calc(280px - 10%);
        }

        &:nth-child(2) {
          width: calc(620px - 10%);
          height: calc(320px - 10%);
        }

        &-circle {
          width: 30px;
          height: 30px;
          top: -15px;
          font-size: 14px;
        }
      }
    }
  }

  @media only screen and (max-width: 768px) {
    &-inner {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &-details {
      margin-left: 0;
    }

    &-disclaimer {
      width: 525px;
      height: 320px;

      &-block {
        &:nth-child(1) {
          width: calc(620px - 30%);
          height: calc(300px - 30%);
        }

        &:nth-child(2) {
          width: calc(620px - 30%);
          height: calc(340px - 30%);
        }

        &-circle {
          left: 50px;
        }
      }
    }

    &-actions {
      &__root {
        margin: 0 auto;
      }
    }
  }

  @media only screen and (max-width: 576px) {
    &-details {
      width: 100%;

      &-inner {
        padding: 0 20px;
      }

      &-title {
        font-size: 28px;
      }

      &-text {
        padding: 0 20px;
      }
    }

    &-disclaimer {
      width: 390px;
      height: 260px;

      &-block {
        &:nth-child(1) {
          width: 320px;
          height: 150px;
        }

        &:nth-child(2) {
          width: 390px;
          height: 196px;
        }

        &-circle {
          width: 25px;
          height: 25px;
          top: -12px;
        }
      }
    }
  }

  @media only screen and (max-width: 425px) {
    &-disclaimer {
      width: 350px;
      height: 230px;

      &-block {
        &:nth-child(1) {
          width: 289px;
          height: 130px;
        }

        &:nth-child(2) {
          width: 351px;
          height: 176px;
        }

        &-circle {
          left: 40px;
        }
      }
    }
  }

  @media only screen and (max-width: 375px) {
    &-disclaimer {
      width: 285px;
      height: 210px;

      &-block {
        &:nth-child(1) {
          width: 255px;
          height: 124px;
        }

        &:nth-child(2) {
          width: 284px;
          height: 157px;
        }
      }
    }
  }
}

.pay-button {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 8px;
  padding: 14px 16px;

  font-weight: 600;

  @media only screen and (max-width: 768px) {
    min-width: 460px;
  }

  @media only screen and (max-width: 576px) {
    min-width: calc(100vw - 40px);
  }
}
</style>
